import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileText, Calculator } from 'lucide-react';
import { AdminEmployee } from '../types/models';
import StaffingConversionSection from './StaffingConversionSection';
import {
  fetchEmployeeReportAssignments,
  isAssignmentActiveOnDate,
  REPORT_ROLE_TO_SUMMARY_CATEGORY,
  type EmployeeReportAssignment
} from './reportAssignments';

const StaffDisclosureTab = React.lazy(() => import('./staff-disclosure/StaffDisclosureTab'));
type ReportTab = 'summary' | 'staffing' | 'disclosure';

const getTodayString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const JOB_CATEGORIES: Record<string, string[]> = {
  '生活相談員': ['生活相談員'],
  '看護職員': ['看護'],
  '介護職員': ['介護'],
  '計画作成担当者': ['管理者', '介護支援専門員'],
  '栄養士': ['栄養士'],
  '調理師': ['厨房', '調理'],
  'その他従業員': ['清掃', '庶務']
};

const CARE_QUALIFICATIONS = [
  { key: '介護福祉士', label: '介護福祉士' },
  { key: '実務者研修', label: '実務者研修' },
  { key: '初任者研修', label: '介護職員初任者研修' },
  { key: '介護支援専門員', label: '介護支援専門員' },
  { key: 'ケアマネージャー', label: '介護支援専門員' }
];

const isFullTime = (emp: AdminEmployee) =>
  emp.employmentType === '正社員' || emp.employmentType === 'その他';

const isActiveAsOf = (emp: AdminEmployee, asOfDate: string): boolean => {
  if (emp.hireDate && emp.hireDate > asOfDate) return false;
  if (emp.retirementDate && emp.retirementDate <= asOfDate) return false;
  if (emp.status === 'retired' && !emp.retirementDate) return false;
  return true;
};

const normalizeDateString = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const normalized = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
};

const calculateFullYears = (
  startValue: string | null | undefined,
  endValue: string
): number | null => {
  const start = normalizeDateString(startValue);
  const end = normalizeDateString(endValue);
  if (!start || !end || start > end) return null;

  const [startYear, startMonth, startDay] = start.split('-').map(Number);
  const [endYear, endMonth, endDay] = end.split('-').map(Number);
  let years = endYear - startYear;
  if (endMonth < startMonth || (endMonth === startMonth && endDay < startDay)) {
    years -= 1;
  }
  return Math.max(0, years);
};

const calculateFullMonths = (
  startValue: string | null | undefined,
  endValue: string
): number | null => {
  const start = normalizeDateString(startValue);
  const end = normalizeDateString(endValue);
  if (!start || !end || start > end) return null;

  const [startYear, startMonth, startDay] = start.split('-').map(Number);
  const [endYear, endMonth, endDay] = end.split('-').map(Number);
  let months = (endYear - startYear) * 12 + (endMonth - startMonth);
  if (endDay < startDay) {
    months -= 1;
  }
  return Math.max(0, months);
};

const formatAverageYears = (values: Array<number | null>): string => {
  const valid = values.filter((v): v is number => v !== null);
  if (valid.length === 0) return '-';
  return `${Math.round(valid.reduce((sum, v) => sum + v, 0) / valid.length)}歳`;
};

const formatAverageMonths = (values: Array<number | null>): string => {
  const valid = values.filter((v): v is number => v !== null);
  if (valid.length === 0) return '-';
  const avgMonths = Math.round(valid.reduce((sum, v) => sum + v, 0) / valid.length);
  return `${Math.floor(avgMonths / 12)}年${avgMonths % 12}ヶ月`;
};

const isCareEmployee = (emp: AdminEmployee): boolean =>
  JOB_CATEGORIES['介護職員'].some(jt => emp.jobType?.includes(jt));

const hasMinimumTenureAsOf = (emp: AdminEmployee, asOfDate: string): boolean => {
  const months = calculateFullMonths(emp.hireDate, asOfDate);
  return months !== null && months >= 12;
};

const SummaryView: React.FC<{ employees: AdminEmployee[] }> = ({ employees }) => {
  const [reportDate, setReportDate] = useState<string>(() => getTodayString());
  const [excludeUnder12Months, setExcludeUnder12Months] = useState<boolean>(false);
  const [reportAssignments, setReportAssignments] = useState<EmployeeReportAssignment[]>([]);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setAssignmentError(null);
      try {
        const assignments = await fetchEmployeeReportAssignments(reportDate, reportDate);
        if (!cancelled) setReportAssignments(assignments);
      } catch (error: any) {
        if (!cancelled) {
          setReportAssignments([]);
          setAssignmentError(error?.message ?? '行政報告用の職種設定を取得できませんでした。');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [reportDate]);

  const reportData = useMemo(() => {
    const activeAsOfEmployees = employees.filter(emp => isActiveAsOf(emp, reportDate));
    const excludedByTenureEmployees = activeAsOfEmployees.filter(
      emp => !hasMinimumTenureAsOf(emp, reportDate)
    );
    const activeEmployees = excludeUnder12Months
      ? activeAsOfEmployees.filter(emp => hasMinimumTenureAsOf(emp, reportDate))
      : activeAsOfEmployees;

    const fullTimeCount = activeEmployees.filter(isFullTime).length;
    const partTimeCount = activeEmployees.filter(emp => !isFullTime(emp)).length;

    const jobCounts: Record<string, { fullTime: number; partTime: number }> = {};
    Object.keys(JOB_CATEGORIES).forEach(category => {
      jobCounts[category] = { fullTime: 0, partTime: 0 };
    });

    const activeAssignmentsByEmployee = new Map<string, EmployeeReportAssignment[]>();
    reportAssignments
      .filter(assignment => isAssignmentActiveOnDate(assignment, reportDate))
      .forEach(assignment => {
        const current = activeAssignmentsByEmployee.get(assignment.employeeId) ?? [];
        current.push(assignment);
        activeAssignmentsByEmployee.set(assignment.employeeId, current);
      });

    activeEmployees.forEach(emp => {
      const employeeAssignments = activeAssignmentsByEmployee.get(emp.id) ?? [];
      if (employeeAssignments.length > 0) {
        const categories = new Set(
          employeeAssignments.map(assignment => REPORT_ROLE_TO_SUMMARY_CATEGORY[assignment.reportRole])
        );
        categories.forEach(category => {
          if (isFullTime(emp)) {
            jobCounts[category].fullTime++;
          } else {
            jobCounts[category].partTime++;
          }
        });
        return;
      }

      let matched = false;
      for (const [category, jobTypes] of Object.entries(JOB_CATEGORIES)) {
        if (jobTypes.some(jt => emp.jobType?.includes(jt))) {
          if (isFullTime(emp)) {
            jobCounts[category].fullTime++;
          } else {
            jobCounts[category].partTime++;
          }
          matched = true;
          break;
        }
      }
      if (!matched) {
        if (isFullTime(emp)) {
          jobCounts['その他従業員'].fullTime++;
        } else {
          jobCounts['その他従業員'].partTime++;
        }
      }
    });

    const careEmployees = activeEmployees.filter(emp => {
      const employeeAssignments = activeAssignmentsByEmployee.get(emp.id) ?? [];
      return employeeAssignments.length > 0
        ? employeeAssignments.some(assignment => assignment.reportRole === 'care')
        : isCareEmployee(emp);
    });
    const careEmployeeIds = new Set(careEmployees.map(emp => emp.id));

    const qualificationCounts: Record<string, { fullTime: number; partTime: number }> = {};
    CARE_QUALIFICATIONS.forEach(q => {
      if (!qualificationCounts[q.label]) {
        qualificationCounts[q.label] = { fullTime: 0, partTime: 0 };
      }
    });

    careEmployees.forEach(emp => {
      const empQuals = emp.qualifications || [];
      CARE_QUALIFICATIONS.forEach(q => {
        if (empQuals.some(eq => eq.includes(q.key))) {
          if (isFullTime(emp)) {
            qualificationCounts[q.label].fullTime++;
          } else {
            qualificationCounts[q.label].partTime++;
          }
        }
      });
    });

    const metricRows = [
      { label: '全職員', employees: activeEmployees },
      { label: '常勤職員', employees: activeEmployees.filter(isFullTime) },
      { label: '常勤介護職員', employees: activeEmployees.filter(emp => isFullTime(emp) && careEmployeeIds.has(emp.id)) },
      { label: '非常勤職員', employees: activeEmployees.filter(emp => !isFullTime(emp)) },
      { label: '非常勤介護職員', employees: activeEmployees.filter(emp => !isFullTime(emp) && careEmployeeIds.has(emp.id)) }
    ].map(row => ({
      label: row.label,
      count: row.employees.length,
      averageAge: formatAverageYears(row.employees.map(emp => calculateFullYears(emp.birthDate, reportDate))),
      averageTenure: formatAverageMonths(row.employees.map(emp => calculateFullMonths(emp.hireDate, reportDate))),
      averageExperience: formatAverageMonths(
        row.employees.map(emp => calculateFullMonths(emp.experienceStartDate, reportDate))
      ),
      missingBirthDateCount: row.employees.filter(emp => !normalizeDateString(emp.birthDate)).length
    }));

    return {
      totalFullTime: fullTimeCount,
      totalPartTime: partTimeCount,
      total: fullTimeCount + partTimeCount,
      activeEmployees,
      jobCounts,
      qualificationCounts,
      metricRows,
      retiredIncludedCount: activeEmployees.filter(emp => emp.status === 'retired').length,
      excludedByTenureEmployees,
      excludedRetiredWithoutDateCount: employees.filter(emp => emp.status === 'retired' && !emp.retirementDate).length
    };
  }, [employees, reportDate, excludeUnder12Months, reportAssignments]);

  const handleExportPDF = async () => {
    const element = document.getElementById('admin-report-content');
    if (!element) return;
    try {
      // @ts-ignore - html2pdf is loaded dynamically
      const html2pdf = (await import('html2pdf.js')).default;
      html2pdf()
        .set({
          margin: 10,
          filename: `行政報告_${reportDate}.pdf`,
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        })
        .from(element)
        .save();
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('PDF出力に失敗しました。');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">基準日:</label>
            <input
              type="date"
              value={reportDate}
              onChange={e => setReportDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700">
            <input
              type="checkbox"
              checked={excludeUnder12Months}
              onChange={e => setExcludeUnder12Months(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>12ヶ月未満を除外</span>
          </label>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Download className="w-4 h-4" />
            PDF出力
          </button>
        </div>
      </div>

      {assignmentError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          行政報告用の兼務設定を反映できませんでした: {assignmentError}
        </div>
      )}

      <div id="admin-report-content" className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">基準日時点サマリー</h3>
            <p className="text-sm text-gray-500 mt-1">
              {reportDate} 時点{excludeUnder12Months ? '（12ヶ月以上在籍者のみ）' : ''}
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">区分</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">人数</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">平均年齢</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">平均勤続年数</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">平均実務経験年数</th>
                    <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">生年月日未登録</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.metricRows.map(row => (
                    <tr key={row.label} className={row.label === '全職員' ? 'font-bold bg-blue-50' : ''}>
                      <td className="border border-gray-300 px-4 py-2">{row.label}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">{row.count}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">{row.averageAge}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">{row.averageTenure}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">{row.averageExperience}</td>
                      <td className="border border-gray-300 px-4 py-2 text-center tabular-nums">
                        {row.missingBirthDateCount || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
                <span className="font-medium text-blue-900">退職済み登録で集計に入っている人数: </span>
                <span className="font-bold text-blue-900">{reportData.retiredIncludedCount}人</span>
              </div>
              {excludeUnder12Months && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                  12ヶ月未満で除外: {reportData.excludedByTenureEmployees.length}人
                </div>
              )}
              {reportData.excludedRetiredWithoutDateCount > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800">
                  退職日未設定の退職済み職員が {reportData.excludedRetiredWithoutDateCount}人います。
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">職種別従業員数</h3>
            <p className="text-sm text-gray-500 mt-1">
              {reportDate} 時点{excludeUnder12Months ? '（12ヶ月以上在籍者のみ）' : ''}。兼務者は該当する各職種へ計上します。
            </p>
          </div>
          <div className="p-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">区分</th>
                  <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">常勤</th>
                  <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">非常勤</th>
                  <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">合計</th>
                </tr>
              </thead>
              <tbody>
                <tr className="font-bold bg-blue-50">
                  <td className="border border-gray-300 px-4 py-2">実人数</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">{reportData.totalFullTime}</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">{reportData.totalPartTime}</td>
                  <td className="border border-gray-300 px-4 py-2 text-center">{reportData.total}</td>
                </tr>
                {Object.entries(reportData.jobCounts).map(([category, counts]) => (
                  <tr key={category}>
                    <td className="border border-gray-300 px-4 py-2">{category}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{counts.fullTime || '-'}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{counts.partTime || '-'}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">
                      {counts.fullTime + counts.partTime || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">従業者である介護職員が有してる資格</h3>
            <p className="text-sm text-gray-500 mt-1">
              {reportDate} 時点{excludeUnder12Months ? '（12ヶ月以上在籍者のみ）' : ''}
            </p>
          </div>
          <div className="p-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">区分</th>
                  <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">常勤</th>
                  <th className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700">非常勤</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-gray-100">
                  <td className="border border-gray-300 px-4 py-2 font-medium">延べ人数</td>
                  <td className="border border-gray-300 px-4 py-2 text-center"></td>
                  <td className="border border-gray-300 px-4 py-2 text-center"></td>
                </tr>
                {Object.entries(reportData.qualificationCounts).map(([qual, counts]) => (
                  <tr key={qual}>
                    <td className="border border-gray-300 px-4 py-2">{qual}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{counts.fullTime}</td>
                    <td className="border border-gray-300 px-4 py-2 text-center">{counts.partTime}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">基準日時点の対象者一覧</h3>
            <p className="text-sm text-gray-500 mt-1">
              年齢・勤続年数・退職者の戻りを確認できます{excludeUnder12Months ? '（12ヶ月以上在籍者のみ）' : ''}。
            </p>
          </div>
          <div className="max-h-[65vh] overflow-auto overscroll-contain p-6">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                <tr className="bg-gray-50">
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">氏名</th>
                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">職種</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">雇用形態</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">登録状態</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">入社日</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">退職日</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">年齢</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">勤続年数</th>
                  <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">実務経験</th>
                </tr>
              </thead>
              <tbody>
                {reportData.activeEmployees.map(emp => (
                  <tr key={emp.id}>
                    <td className="border border-gray-300 px-3 py-2">{emp.name}</td>
                    <td className="border border-gray-300 px-3 py-2">{emp.jobType}</td>
                    <td className="border border-gray-300 px-3 py-2 text-center">{emp.employmentType}</td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {emp.status === 'retired' ? '退職済み登録' : '在職中登録'}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {normalizeDateString(emp.hireDate) ?? '-'}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center">
                      {normalizeDateString(emp.retirementDate) ?? '-'}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center tabular-nums">
                      {(() => {
                        const age = calculateFullYears(emp.birthDate, reportDate);
                        return age === null ? '-' : `${age}歳`;
                      })()}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center tabular-nums">
                      {formatAverageMonths([calculateFullMonths(emp.hireDate, reportDate)])}
                    </td>
                    <td className="border border-gray-300 px-3 py-2 text-center tabular-nums">
                      {formatAverageMonths([calculateFullMonths(emp.experienceStartDate, reportDate)])}
                    </td>
                  </tr>
                ))}
                {reportData.activeEmployees.length === 0 && (
                  <tr>
                    <td colSpan={9} className="border border-gray-300 px-4 py-6 text-center text-gray-500">
                      基準日時点の対象者がいません。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {excludeUnder12Months && reportData.excludedByTenureEmployees.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">12ヶ月未満で除外された職員</h3>
              <p className="text-sm text-gray-500 mt-1">{reportDate} 時点</p>
            </div>
            <div className="max-h-[65vh] overflow-auto overscroll-contain p-6">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-30 bg-gray-50 shadow-sm">
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">氏名</th>
                    <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">職種</th>
                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">雇用形態</th>
                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">入社日</th>
                    <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">勤続年数</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.excludedByTenureEmployees.map(emp => (
                    <tr key={emp.id}>
                      <td className="border border-gray-300 px-3 py-2">{emp.name}</td>
                      <td className="border border-gray-300 px-3 py-2">{emp.jobType}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center">{emp.employmentType}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        {normalizeDateString(emp.hireDate) ?? '-'}
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center tabular-nums">
                        {formatAverageMonths([calculateFullMonths(emp.hireDate, reportDate)])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminReportTabbed: React.FC<{ employees: AdminEmployee[] }> = ({ employees }) => {
  const [reportTab, setReportTab] = useState<ReportTab>('summary');
  const [disclosureVisited, setDisclosureVisited] = useState(false);

  return (
    <div className="space-y-6">
      {/* サブタブ */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
        <button
          type="button"
          onClick={() => setReportTab('summary')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            reportTab === 'summary'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="w-4 h-4" />
          集計レポート
        </button>
        <button
          type="button"
          onClick={() => setReportTab('staffing')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            reportTab === 'staffing'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Calculator className="w-4 h-4" />
          常勤換算
        </button>
        <button
          type="button"
          onClick={() => { setDisclosureVisited(true); setReportTab('disclosure'); }}
          className={`flex shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            reportTab === 'disclosure'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="w-4 h-4" />
          重説用職員集計
        </button>
      </div>

      {reportTab === 'summary' && <SummaryView employees={employees} />}
      {reportTab === 'staffing' && <StaffingConversionSection employees={employees} />}
      {disclosureVisited && <div hidden={reportTab !== 'disclosure'}><React.Suspense fallback={<p className="text-sm text-slate-600">重説用職員集計を読み込んでいます...</p>}><StaffDisclosureTab /></React.Suspense></div>}
    </div>
  );
};

export default AdminReportTabbed;
